import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useMoneyFmt } from "@/lib/company";
import { useCompany } from "@/lib/company";
import { useAuth } from "@/lib/auth";
import { TID } from "@/constants/testIds";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { emitAction, useActionListener } from "@/lib/createBus";
import {
  Sparkles, Zap, AlertTriangle, TrendingUp, Wand2, FileCheck2, Bot, ArrowRight,
  Wallet2, FileText, Receipt as ReceiptIcon, Activity, BellRing, ScrollText,
  FileText as FileWarning, FileText as ReceiptText, ChevronLeft, ChevronRight,
} from "lucide-react";

// -------- Timeframe helpers ---------------------------------------
// The Dashboard "Income snapshot" defaults to YTD but users asked for a way
// to step back through prior months / years. Rather than build a full date
// picker we ship three simple modes ("ytd", "month", "year") with left/right
// arrows to shift the anchor month. All range math is derived from a single
// anchor `YYYY-MM` string.
function monthAnchor(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function shiftAnchor(anchor, delta, mode) {
  const [y, m] = anchor.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  if (mode === "year") d.setFullYear(d.getFullYear() + delta);
  else d.setMonth(d.getMonth() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function rangeFor(anchor, mode) {
  const [y, m] = anchor.split("-").map(Number);
  if (mode === "year") {
    return { start: `${y}-01-01`, end: `${y}-12-31` };
  }
  if (mode === "ytd") {
    const now = new Date();
    return {
      start: `${now.getFullYear()}-01-01`,
      end: now.toISOString().slice(0, 10),
    };
  }
  // month
  const last = new Date(y, m, 0).getDate();
  return {
    start: `${y}-${String(m).padStart(2, "0")}-01`,
    end: `${y}-${String(m).padStart(2, "0")}-${String(last).padStart(2, "0")}`,
  };
}
function labelFor(anchor, mode) {
  if (mode === "ytd") return "YTD";
  const [y, m] = anchor.split("-").map(Number);
  if (mode === "year") return `${y}`;
  const monthName = new Date(y, m - 1, 1).toLocaleString("en-US", { month: "long" });
  return `${monthName} ${y}`;
}
import FirstConnectWelcome from "@/components/FirstConnectWelcome";
import MinerBanner from "@/components/MinerBanner";
import FirmAtAGlance from "@/components/FirmAtAGlance";
import BusinessOverview from "@/components/BusinessOverview";
import DashboardTodos from "@/components/DashboardTodos";
import ReorderAlertsTile from "@/components/ReorderAlertsTile";
import WelcomeModal, { hasSeenWelcome, markWelcomeSeen, ReplayWelcomeButton } from "@/components/WelcomeModal";
import PostOnboardingTour, { hasSeenPostOnboarding, markPostOnboardingSeen } from "@/components/PostOnboardingTour";
import PendingReviewCard from "@/components/PendingReviewCard";
import { LayoutGrid, Sparkle, Grid3x3, GraduationCap, CheckSquare } from "lucide-react";

const kindLabel = {
  categorize: "Transactions Categorized",
  post_je: "Journal Entries Auto-Posted",
  flag_review: "Flagged for Review",
  rule_created: "Rules Created",
  coa_generated: "CoA Accounts Suggested",
  veryfi_ocr: "Statement Lines OCR'd",
  webhook_sync: "Webhook Auto-Syncs",
};

const kindIcon = {
  categorize: Zap, post_je: FileCheck2, flag_review: AlertTriangle,
  rule_created: Wand2, coa_generated: Sparkles, veryfi_ocr: Bot,
  webhook_sync: Zap,
};

const kindColor = {
  categorize: "#6366F1", post_je: "#10B981", flag_review: "#F97316",
  rule_created: "#6366F1", coa_generated: "#3B82F6", veryfi_ocr: "#3B82F6",
  webhook_sync: "#6366F1",
};

export default function Dashboard() {
  const { currentId, current, refresh } = useCompany();
  const { user } = useAuth();
  const [totals, setTotals] = useState(null);
  const [activity, setActivity] = useState([]);
  const [income, setIncome] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [attention, setAttention] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  // Shared todos state — powers the DashboardTodos card AND drives whether
  // the Needs-your-attention shimmer is suppressed (during Setup mode the
  // shimmer moves onto the checklist step instead).
  const [todos, setTodos] = useState(null);
  // Dashboard view mode — "classic" (original) or "firm" (QBO-style Firm at
  // a Glance). Persisted per-user in localStorage so it sticks across sessions.
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("dashboard_view") || "classic"; }
    catch { return "classic"; }
  });
  const changeView = useCallback((m) => {
    setViewMode(m);
    try { localStorage.setItem("dashboard_view", m); } catch { /* ignore */ }
  }, []);

  // First-time welcome tour + replay button. Only fires for clients
  // (Pros / superadmin get a different orientation). Persisted per-user.
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  // When true, closing the welcome modal chains straight into the
  // post-onboarding dashboard tour. Used by the Replay button so one
  // click walks the client back through Welcome → Post-Onboarding.
  const [chainPostTour, setChainPostTour] = useState(false);
  // Post-onboarding dashboard tour — fires only for clients on the very
  // first dashboard visit after `onboarding_complete` flips to true.
  // Persisted per (user, company) so it never fires twice per company.
  const [postTourOpen, setPostTourOpen] = useState(false);
  useEffect(() => {
    if (!user?.id) return;
    // Restrict to client role — Pros already know the app.
    if (user.role !== "client") return;
    // Skip every onboarding overlay for public demo visitors. First
    // impression on `/demo/uk` needs to be the actual dashboard —
    // not a "Congratulations, UK!" modal that makes zero sense to
    // someone who arrived from a landing page.
    if (user.is_demo_visitor) return;
    if (!hasSeenWelcome(user.id)) {
      // Delay a beat so the dashboard has painted first — reveals the
      // real UI briefly before the overlay drops (matches RocketBooks).
      const t = setTimeout(() => setWelcomeOpen(true), 500);
      return () => clearTimeout(t);
    }
  }, [user?.id, user?.role]);
  // Post-onboarding tour trigger — client only, onboarding_complete=true,
  // not-yet-seen for this (user, company) pair. Guarded so it doesn't
  // fight the WelcomeModal: only fires once welcome has been marked seen.
  useEffect(() => {
    if (!user?.id) return;
    if (user.role !== "client") return;
    if (user.is_demo_visitor) return;  // demo visitors skip every tour
    if (!current?.id || !current?.onboarding_complete) return;
    if (welcomeOpen) return;
    // Wait for the welcome tour to be fully complete before firing the
    // post-onboarding tour. Otherwise the two modals race each other on
    // first login and both render simultaneously.
    if (!hasSeenWelcome(user.id)) return;
    if (hasSeenPostOnboarding(user.id, current.id)) return;
    const t = setTimeout(() => setPostTourOpen(true), 400);
    return () => clearTimeout(t);
  }, [user?.id, user?.role, current?.id, current?.onboarding_complete, welcomeOpen]);
  const closeWelcome = () => {
    setWelcomeOpen(false);
    if (user?.id) markWelcomeSeen(user.id);
    // If Replay was clicked, chain straight into the post-onboarding tour
    // regardless of whether it's been seen before (option 3 — replay
    // covers everything).
    if (chainPostTour) {
      setChainPostTour(false);
      setTimeout(() => setPostTourOpen(true), 200);
    }
  };
  const closePostTour = () => {
    setPostTourOpen(false);
    if (user?.id && current?.id) markPostOnboardingSeen(user.id, current.id);
  };
  const replayWelcome = () => {
    setChainPostTour(true);
    setWelcomeOpen(true);
  };
  // Independent replay entry for JUST the post-onboarding tour — used
  // by the Settings-page replay buttons. Does NOT chain to welcome.
  const replayPostTour = () => setPostTourOpen(true);
  // Read `?replay=welcome` or `?replay=post-tour` from the URL and fire
  // the matching tour. Used by the "Replay tour" buttons on the
  // Settings page, which navigate here rather than dispatching a global
  // event (URL is more shareable + survives a hard refresh).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const flag = searchParams.get("replay");
    if (!flag) return;
    // Strip the param first so the tour doesn't re-open on every
    // subsequent re-render / navigation.
    const next = new URLSearchParams(searchParams);
    next.delete("replay");
    setSearchParams(next, { replace: true });
    if (flag === "welcome") {
      // Just replay welcome — don't chain into post-tour (Settings
      // exposes them as two separate buttons intentionally).
      setChainPostTour(false);
      setWelcomeOpen(true);
    } else if (flag === "post-tour") {
      setPostTourOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  // Income-snapshot timeframe — user asked for a way to step back through
  // prior months / years. `mode` is one of "ytd" | "month" | "year";
  // `anchor` is a YYYY-MM string that arrow-navigates within the chosen mode.
  const [tfMode, setTfMode] = useState("ytd");
  const [tfAnchor, setTfAnchor] = useState(() => monthAnchor(0));
  // Track the last-observed sync status so we can trigger a heavy refetch
  // exactly once when a background sync transitions from `syncing → idle`.
  const prevStatusRef = useRef(null);

  useEffect(() => {
    if (!currentId) return;
    let cancelled = false;

    const { start, end } = rangeFor(tfAnchor, tfMode);

    // -------- Heavy fetch (ai/activity + reports + metrics) ------------
    // Server responses are already 15s-cached, so this is safe to call
    // fairly often, but we still gate it — see poll strategy below.
    const fetchHeavy = () => {
      api.get(`/companies/${currentId}/ai/activity`).then(r => {
        if (cancelled) return;
        setTotals(r.data.totals); setActivity(r.data.activity);
      }).catch(() => {});
      api.get(`/companies/${currentId}/reports/income-statement`, {
        params: { start, end },
      })
        .then(r => { if (!cancelled) setIncome(r.data); }).catch(() => {});
      api.get(`/companies/${currentId}/dashboard/metrics`)
        .then(r => { if (!cancelled) setMetrics(r.data); }).catch(() => {});
      api.get(`/companies/${currentId}/dashboard/attention`)
        .then(r => { if (!cancelled) setAttention(r.data); }).catch(() => {});
      // Todos live inside the firm-glance payload (cached 15s server-side).
      // Fetching here lets us share the state between DashboardTodos and
      // the Needs-your-attention shimmer suppression logic.
      // On failure we set `todos = false` (distinct from `null` = still
      // loading) so DashboardTodos can render an inline error state
      // instead of an infinite skeleton.
      api.get(`/companies/${currentId}/dashboard/firm-glance`)
        .then(r => { if (!cancelled) setTodos(r.data?.todos || false); })
        .catch(() => { if (!cancelled) setTodos(false); });
    };

    // -------- Cheap sync-status poll (single Mongo lookup) -------------
    // Also drives the heavy refetch when a sync completes.
    const fetchStatus = async () => {
      try {
        const r = await api.get(`/companies/${currentId}/sync-status`);
        if (cancelled) return;
        const next = r.data;
        const prev = prevStatusRef.current;
        setSyncStatus(next);
        // Fire a heavy refetch whenever any of these signals fire:
        //   • the pill flips syncing → idle (existing behavior), OR
        //   • total_txns changed (new webhook rows landed even without a
        //     visible syncing state — e.g. a very fast sync that finishes
        //     between two 5-second polls).
        // Second condition is what fixed the "Dashboard is slow to
        // populate right after Plaid Link" complaint — without it, we'd
        // sit on stale zeros for up to 120s (the safety-net interval).
        const flipped = prev && prev.status === "syncing" && next.status !== "syncing";
        const rowsChanged = (prev?.total_txns ?? -1) !== (next.total_txns ?? -1);
        if (flipped || rowsChanged) fetchHeavy();
        prevStatusRef.current = next;
      } catch { /* ignore */ }
    };

    // Initial burst
    fetchHeavy();
    fetchStatus();

    // Adaptive polling: cheap pill every 5s while syncing, 15s while idle;
    // heavy re-fetch runs only when the pill flips syncing→idle, on tab
    // focus, or as a slow 120s safety net.
    const heavyEvery = 120_000;
    const heavyInterval = setInterval(fetchHeavy, heavyEvery);
    let statusTimer;
    const tickStatus = () => {
      fetchStatus();
      const delay = prevStatusRef.current?.status === "syncing" ? 5_000 : 15_000;
      statusTimer = setTimeout(tickStatus, delay);
    };
    statusTimer = setTimeout(tickStatus, 5_000);

    // Immediate refresh when the tab regains focus.
    const onFocus = () => {
      if (document.visibilityState !== "visible") return;
      fetchStatus();
      fetchHeavy();
    };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      clearInterval(heavyInterval);
      clearTimeout(statusTimer);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [currentId, tfMode, tfAnchor]);

  if (!current) return <div className="text-slate-500">Select a company to view your Dashboard.</div>;

  if (!current.onboarding_complete) {
    return <OnboardingNudge company={current} refresh={refresh} nudgeWelcomeOpen={welcomeOpen} onCloseWelcome={closeWelcome} onReplay={replayWelcome} showReplay={user?.role === "client"} />;
  }

  return (
    <div className="space-y-6" data-testid={TID.aiPulseSection}>
      <PostOnboardingTour
        open={postTourOpen}
        companyName={current?.name}
        companyId={currentId}
        todos={todos}
        hasData={(syncStatus?.total_txns || 0) > 0 || (totals?.transactions || 0) > 0}
        onSwitchView={changeView}
        onDone={closePostTour}
      />
      <FirstConnectWelcome
        status={syncStatus}
        companyId={currentId}
        companyName={current?.name}
      />
      <MinerBanner />

      <PendingReviewCard companyId={currentId} />

      {/* View toggle — Classic vs Firm at a Glance, plus a one-way
          "Onboarding" jump to restart / continue the setup wizard.
          The wizard remembers where the user left off; already-
          complete companies can rerun it (e.g. to reconnect Plaid,
          re-run the AI-assisted CoA, or revisit the housekeeping
          toggles + summary). */}
      <div className="flex justify-end items-center gap-2">
        <div
          role="tablist"
          aria-label="Dashboard view"
          className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 p-0.5"
          data-testid="dashboard-view-toggle"
        >
          {[
            { key: "classic", label: "Classic", Icon: LayoutGrid },
            { key: "firm",    label: "Firm at a Glance", Icon: Sparkle },
            { key: "business", label: "Business Overview", Icon: Grid3x3 },
          ].map(({ key, label, Icon }) => (
            <button
              key={key}
              role="tab"
              aria-selected={viewMode === key}
              onClick={() => changeView(key)}
              data-testid={`dashboard-view-${key}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                viewMode === key
                  ? "bg-white text-slate-900 shadow-sm border border-slate-200"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <Icon size={12} />
              {label}
            </button>
          ))}
        </div>
        <Link
          to="/accounting/todo"
          data-testid="dashboard-view-cockpit"
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors shadow-sm"
          title="Open the accounting Cockpit"
        >
          <CheckSquare size={12} />
          Cockpit
        </Link>
        <Link
          to="/onboarding"
          data-testid="dashboard-view-onboarding"
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors shadow-sm"
          title="Run the onboarding wizard again"
        >
          <GraduationCap size={12} />
          Onboarding
        </Link>
      </div>

      <DashboardTodos todos={todos} />

      <ReorderAlertsTile currentId={currentId} />

      {viewMode === "firm" ? (
        <FirmAtAGlance userName={user?.name || user?.email} />
      ) : viewMode === "business" ? (
        <BusinessOverview />
      ) : (
        <ClassicDashboard
          current={current}
          attention={attention}
          totals={totals}
          income={income}
          metrics={metrics}
          activity={activity}
          tfMode={tfMode}
          tfAnchor={tfAnchor}
          setTfMode={setTfMode}
          setTfAnchor={setTfAnchor}
          todos={todos}
          welcomeOpen={welcomeOpen}
          onCloseWelcome={closeWelcome}
          onReplayWelcome={replayWelcome}
          showWelcomeReplay={user?.role === "client"}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------
// Classic dashboard content (extracted from the original render so we
// can toggle between it and Firm at a Glance without duplicating state
// or data-fetching logic above).
// -----------------------------------------------------------------
function ClassicDashboard({
  current, attention, totals, income, metrics, activity,
  tfMode, tfAnchor, setTfMode, setTfAnchor,
  todos,
  welcomeOpen, onCloseWelcome, onReplayWelcome, showWelcomeReplay,
}) {
  const fmtMoney = useMoneyFmt();
  // During Setup mode we move the "highlight" rainbow off the Needs-your-
  // attention section and onto the checklist itself (that's where the user
  // should look first). In Close mode we leave the Needs-your-attention
  // shimmer alone — it's the primary priority cue in steady state.
  const suppressAttentionShimmer =
    todos?.mode === "setup" && todos?.visible && !todos?.is_complete;
  return (
    <>
      <WelcomeModal open={welcomeOpen} onClose={onCloseWelcome} />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">
            What the AI has done for {current.name} · {current.reporting_basis} basis
          </p>
        </div>
        {showWelcomeReplay && (
          <ReplayWelcomeButton onClick={onReplayWelcome} />
        )}
      </div>

      <AttentionTile attention={attention} suppressShimmer={suppressAttentionShimmer} />

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            ["Transactions", totals.transactions, "#6366F1"],
            ["Auto-posted", totals.posted, "#10B981"],
            ["Needs review", totals.flagged, "#F97316"],
            ["AI accuracy", `${totals.accuracy}%`, "#10B981"],
          ].map(([label, val, col]) => (
            <div key={label} className="rounded-xl border bg-white p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
              <div className="font-heading text-3xl font-bold mt-1" style={{ color: col }}>{val}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border bg-white p-5">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <TrendingUp size={16} className="text-slate-500" />
              <h2 className="font-heading font-semibold">
                Income snapshot · {labelFor(tfAnchor, tfMode)}
              </h2>
            </div>
            <TimeframePicker
              mode={tfMode}
              anchor={tfAnchor}
              onModeChange={(m) => {
                setTfMode(m);
                // Reset anchor to current period whenever mode changes so
                // "This month" / "This year" always start where the user
                // expects, regardless of where they'd navigated to.
                setTfAnchor(monthAnchor(0));
              }}
              onShift={(delta) => setTfAnchor(a => shiftAnchor(a, delta, tfMode))}
              onReset={() => setTfAnchor(monthAnchor(0))}
            />
          </div>
          {income ? (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="text-xs text-slate-500 uppercase">Revenue</div>
                <div className="font-mono-num text-2xl font-semibold text-emerald-600">{fmtMoney(income.total_revenue)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase">Expenses</div>
                <div className="font-mono-num text-2xl font-semibold text-orange-600">{fmtMoney(income.total_expense)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase">Net Income</div>
                <div className="font-mono-num text-2xl font-semibold text-slate-900">{fmtMoney(income.net_income)}</div>
              </div>
            </div>
          ) : <div className="text-sm text-slate-500">Loading…</div>}
          <div className="mt-4 flex gap-2">
            <Link to="/reports/income-statement" className="text-xs text-slate-600 hover:text-slate-900 underline">Open income statement</Link>
            <Link to="/reports/balance-sheet" className="text-xs text-slate-600 hover:text-slate-900 underline">Balance sheet</Link>
          </div>

          {metrics && (
            <div className="mt-5 pt-4 border-t grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dashboard-metrics">
              <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold flex items-center gap-1">
                  <Wallet2 size={11} /> Cash on hand
                </div>
                <div className="font-mono-num font-semibold text-emerald-700 text-lg mt-0.5">{fmtMoney(metrics.cash_on_hand)}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">Across all bank accounts</div>
              </div>
              <Link to="/invoices" className="rounded-lg bg-indigo-50 border border-indigo-100 p-3 hover:border-indigo-300 transition">
                <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold flex items-center gap-1">
                  <FileText size={11} /> Outstanding A/R
                </div>
                <div className="font-mono-num font-semibold text-indigo-700 text-lg mt-0.5">{fmtMoney(metrics.outstanding_invoices)}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {metrics.invoice_count} open invoice{metrics.invoice_count === 1 ? "" : "s"}
                  {metrics.overdue_invoices > 0 && <span className="text-red-600"> · {fmtMoney(metrics.overdue_invoices)} overdue</span>}
                </div>
              </Link>
              <Link to="/bills" className="rounded-lg bg-orange-50 border border-orange-100 p-3 hover:border-orange-300 transition">
                <div className="text-[10px] uppercase tracking-wider text-orange-700 font-semibold flex items-center gap-1">
                  <ReceiptIcon size={11} /> Outstanding A/P
                </div>
                <div className="font-mono-num font-semibold text-orange-700 text-lg mt-0.5">{fmtMoney(metrics.outstanding_bills)}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {metrics.bill_count} open bill{metrics.bill_count === 1 ? "" : "s"}
                  {metrics.overdue_bills > 0 && <span className="text-red-600"> · {fmtMoney(metrics.overdue_bills)} overdue</span>}
                </div>
              </Link>
              <Link to="/accounting/transactions" className="rounded-lg bg-slate-50 border border-slate-200 p-3 hover:border-slate-400 transition">
                <div className="text-[10px] uppercase tracking-wider text-slate-700 font-semibold flex items-center gap-1">
                  <Activity size={11} /> Cash activity · 30d
                </div>
                <div className={`font-mono-num font-semibold text-lg mt-0.5 ${metrics.net_cash_30d >= 0 ? "text-emerald-700" : "text-orange-700"}`}>
                  {metrics.net_cash_30d >= 0 ? "+" : ""}{fmtMoney(metrics.net_cash_30d)}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {fmtMoney(metrics.cash_in_30d)} in · {fmtMoney(metrics.cash_out_30d)} out · {metrics.activity_count_30d} txns
                </div>
              </Link>
            </div>
          )}
        </div>

        <div className="rounded-xl border bg-white p-5 flex flex-col max-h-[420px]">
          <div className="flex items-center gap-2 mb-3 shrink-0">
            <Bot size={16} className="text-slate-500" />
            <h2 className="font-heading font-semibold">AI activity</h2>
          </div>
          <div className="space-y-2 overflow-y-auto pr-1 -mr-1 flex-1">
            {activity.map(a => {
              const Icon = kindIcon[a.type] || Sparkles;
              return (
                <div key={a.id} data-testid={TID.aiActivityCard} className="flex items-center gap-3 rounded-md border px-3 py-2">
                  <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: `${kindColor[a.type] || "#6366F1"}18` }}>
                    <Icon size={15} style={{ color: kindColor[a.type] || "#6366F1" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{kindLabel[a.type] || a.type}</div>
                  </div>
                  <div className="font-mono-num font-semibold text-slate-800">{a.count}</div>
                </div>
              );
            })}
            {!activity.length && <div className="text-xs text-slate-500">No AI activity yet.</div>}
          </div>
        </div>
      </div>

      <div className="rounded-xl border bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-semibold">Next steps</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Link to="/accounting/transactions?filter=review" className="rounded-md border p-3 hover:border-slate-400 transition">
            <div className="text-sm font-medium">Review flagged transactions</div>
            <div className="text-xs text-slate-500">The AI needs your call on {totals?.flagged || 0} items.</div>
          </Link>
          <Link to="/accounting/rules" className="rounded-md border p-3 hover:border-slate-400 transition">
            <div className="text-sm font-medium">Turn approvals into rules</div>
            <div className="text-xs text-slate-500">Automate categorization for repeat merchants.</div>
          </Link>
          <Link to="/reports" className="rounded-md border p-3 hover:border-slate-400 transition">
            <div className="text-sm font-medium">Download month-end reports</div>
            <div className="text-xs text-slate-500">PDF-ready CPA statements — one click.</div>
          </Link>
        </div>
      </div>
    </>
  );
}

function AttentionTile({ attention, suppressShimmer = false }) {
  if (!attention) return null;
  const {
    flagged_count: flagged = 0,
    suggested_rules_count: rules = 0,
    overdue_invoices_count: ovInv = 0,
    overdue_bills_count: ovBill = 0,
    unreconciled_accounts_count: unrecon = 0,
    unreconciled_accounts = [],
    staleness_days = 45,
  } = attention;

  const total = ovInv + ovBill;
  // Priority for the single "rainbow shimmer" card. Order matters — only
  // the FIRST bucket in this list that has count > 0 lights up so the
  // user's eye lands on the most urgent action first.
  //
  // When `suppressShimmer` is set (e.g. during Setup mode where the
  // shimmer belongs on the checklist), we zero out `priorityKey` so no
  // attention card gets highlighted — cleaner than two competing
  // shimmers on-screen.
  const priorityKey = suppressShimmer ? null : (
    ovBill > 0 ? "ovBill" :
    ovInv  > 0 ? "ovInv"  : null
  );
  if (total === 0) {
    return (
      <div
        className="rounded-xl border bg-emerald-50/60 border-emerald-200 p-4 flex items-center gap-3"
        data-testid="attention-tile-empty"
      >
        <FileCheck2 size={18} className="text-emerald-600" />
        <div className="text-sm text-emerald-900">
          <b>All clear.</b> No overdue invoices or bills.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-white overflow-hidden" data-testid="attention-tile">
      <div className="px-5 py-3 border-b bg-amber-50/60 flex items-center gap-2">
        <BellRing size={16} className="text-amber-700" />
        <h2 className="font-heading font-semibold text-sm">Needs your attention</h2>
        <span className="text-[11px] text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded-full ml-1">
          {total} item{total === 1 ? "" : "s"}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x">
        <AttentionCard
          testid="attention-overdue-invoices"
          to="/invoices?filter=overdue"
          icon={FileWarning}
          tone={ovInv > 0 ? "rose" : "muted"}
          count={ovInv}
          label="Overdue invoices"
          hint={ovInv > 0 ? "Past-due customer invoices" : "All paid or current"}
          highlight={priorityKey === "ovInv"}
        />
        <AttentionCard
          testid="attention-overdue-bills"
          to="/bills?filter=overdue"
          icon={ReceiptText}
          tone={ovBill > 0 ? "rose" : "muted"}
          count={ovBill}
          label="Overdue bills"
          hint={ovBill > 0 ? "Past-due vendor bills" : "All paid or current"}
          highlight={priorityKey === "ovBill"}
        />
      </div>
    </div>
  );
}

const TONE_CLS = {
  amber:  { bg: "hover:bg-amber-50",  fg: "text-amber-700",  ring: "bg-amber-100" },
  indigo: { bg: "hover:bg-indigo-50", fg: "text-indigo-700", ring: "bg-indigo-100" },
  rose:   { bg: "hover:bg-rose-50",   fg: "text-rose-700",   ring: "bg-rose-100" },
  muted:  { bg: "hover:bg-slate-50",  fg: "text-slate-500",  ring: "bg-slate-100" },
};

function AttentionCard({ testid, to, icon: Icon, tone, count, label, hint, highlight }) {
  const t = TONE_CLS[tone] || TONE_CLS.muted;
  // When highlight === true we swap the default divider styling for a
  // 3px rainbow border + shimmer (see .attention-rainbow in index.css).
  // Adding relative + z-10 pulls the card above the sibling dividers so
  // the border isn't clipped by the parent's `divide-x` rule.
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`px-5 py-4 flex items-start gap-3 transition ${t.bg} ${
        highlight ? "attention-rainbow relative z-10" : ""
      }`}
    >
      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${t.ring}`}>
        <Icon size={16} className={t.fg} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className={`text-2xl font-bold tabular-nums ${count > 0 ? "text-slate-900" : "text-slate-400"}`}>
            {count}
          </span>
          <span className="text-sm font-medium text-slate-700">{label}</span>
        </div>
        <div className="text-xs text-slate-500 mt-0.5 truncate" title={hint}>{hint}</div>
      </div>
      <ArrowRight size={14} className="text-slate-400 mt-2 flex-shrink-0" />
    </Link>
  );
}



// -------- Timeframe picker (arrows + mode dropdown) ------------------
// Lightweight header control for the Income Snapshot section. Purely
// presentational — it just fires callbacks the parent uses to update state
// and re-fetch the report.
function TimeframePicker({ mode, anchor, onModeChange, onShift, onReset }) {
  const isYTD = mode === "ytd";
  return (
    <div
      className="flex items-center gap-1 rounded-lg border bg-slate-50 p-1"
      data-testid="dashboard-timeframe"
    >
      <button
        type="button"
        onClick={() => onShift(-1)}
        disabled={isYTD}
        className="h-7 w-7 flex items-center justify-center rounded-md hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition"
        aria-label="Previous period"
        title="Previous"
        data-testid="dashboard-timeframe-prev"
      >
        <ChevronLeft size={14} className="text-slate-600" />
      </button>
      <select
        value={mode}
        onChange={(e) => onModeChange(e.target.value)}
        className="h-7 px-2 text-xs bg-transparent focus:outline-none cursor-pointer font-medium text-slate-700"
        data-testid="dashboard-timeframe-mode"
      >
        <option value="ytd">Year to date</option>
        <option value="month">By month</option>
        <option value="year">By year</option>
      </select>
      <button
        type="button"
        onClick={() => onShift(1)}
        disabled={isYTD}
        className="h-7 w-7 flex items-center justify-center rounded-md hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition"
        aria-label="Next period"
        title="Next"
        data-testid="dashboard-timeframe-next"
      >
        <ChevronRight size={14} className="text-slate-600" />
      </button>
      {!isYTD && anchor !== monthAnchor(0) && (
        <button
          type="button"
          onClick={onReset}
          className="h-7 px-2 text-[11px] text-slate-500 hover:text-slate-900 hover:bg-white rounded-md transition"
          title="Jump to current period"
          data-testid="dashboard-timeframe-reset"
        >
          Today
        </button>
      )}
    </div>
  );
}


// Onboarding not done → show the "let's finish onboarding" card AND fire a
// live-accountant greeting into the AI panel. If the user replies "yes" /
// "ok" / "sure" / "let's go" in the chat, we navigate them straight into
// /onboarding. Otherwise the existing manual button still works.
function OnboardingNudge({ company, refresh, nudgeWelcomeOpen, onCloseWelcome, onReplay, showReplay }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const greetedRef = useRef(new Set());

  // Fire the AI greeting once per company, then send the user straight
  // into the wizard. All onboarding fields (business type, description,
  // reporting basis, industry, etc.) now live inside /onboarding as
  // step-by-step pages — the dashboard no longer holds a duplicate
  // form. We still respect the WelcomeModal: the redirect waits for it
  // to close (or for it to have been seen already) so first-time users
  // aren't yanked off the modal.
  useEffect(() => {
    if (!company?.id) return;
    if (greetedRef.current.has(company.id)) return;
    greetedRef.current.add(company.id);
    emitAction("ai-open");
    const firstName = (user?.name || "").split(" ")[0];
    const hello = firstName ? `Hi ${firstName} — ` : "Welcome — ";
    setTimeout(() => {
      emitAction("onboarding-coach-greet", {
        message: `${hello}**${company.name}** still needs a quick onboarding to get its books ready. Ready to knock it out? Say **yes** and I'll take you there.`,
      });
    }, 500);
  }, [company?.id, company?.name, user?.name]);

  // Chat-driven affirmative → jump into the wizard.
  useActionListener("onboarding-user-message", (payload) => {
    const t = (payload?.text || "").toLowerCase().trim();
    if (!t) return;
    if (/^(yes|yeah|yep|yup|sure|ok|okay|let'?s (?:go|do it|start)|ready|go)\b/.test(t)) {
      emitAction("onboarding-coach-greet", { message: "Great — heading to onboarding now." });
      setTimeout(() => navigate("/onboarding"), 800);
    }
  });

  // Auto-redirect into the wizard as soon as (a) the company is known
  // and (b) the WelcomeModal is either closed or was never opened. Users
  // land on step 0 ("Business basics") of /onboarding.
  useEffect(() => {
    if (!company?.id) return;
    if (nudgeWelcomeOpen) return;
    const t = setTimeout(() => navigate("/onboarding"), 400);
    return () => clearTimeout(t);
  }, [company?.id, nudgeWelcomeOpen, navigate]);

  return (
    <div className="max-w-2xl mx-auto">
      <WelcomeModal open={nudgeWelcomeOpen} onClose={onCloseWelcome} />
      {showReplay && (
        <div className="flex justify-end mb-3">
          <ReplayWelcomeButton onClick={onReplay} />
        </div>
      )}
      <div className="rounded-xl border bg-white p-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <Sparkles className="text-indigo-600" size={20} />
          </div>
          <h1 className="font-heading text-2xl font-bold">Let's finish onboarding {company.name}</h1>
        </div>
        <p className="text-slate-500 text-sm">Taking you to the wizard…</p>
      </div>
    </div>
  );
}
